/**
 * Cogo JSON -> Turtle RDF serialization for offline editor validation.
 *
 * The serializer intentionally covers the hot visual-editing surface:
 * element identity/type, visual token fields, and child relationships. It is
 * small enough to run on every editor tick and conservative enough that richer
 * schema-specific RDF can be layered in later without changing the public API.
 */

const STYLE_PREDICATES: Array<[key: string, predicate: string]> = [
  ["background", "cogo:tokenBackground"],
  ["color", "cogo:tokenColor"],
  ["padding", "cogo:tokenPadding"],
  ["margin", "cogo:tokenMargin"],
  ["borderRadius", "cogo:tokenRadius"],
  ["width", "cogo:layoutWidth"],
  ["height", "cogo:layoutHeight"],
  ["backdropBlur", "cogo:tokenBackdropBlur"],
  ["shadow", "cogo:tokenShadow"],
  ["borderColor", "cogo:tokenBorderColor"],
]

export function serializeCogoJsonToTurtle(block: any): string {
  let rdf = `# Cogo offline validation ABox.
# Automatically compiled from Cogo JSON for local ontology conformance checks.

@prefix rdf:  <http://www.w3.org/1999/02/22-rdf-syntax-ns#> .
@prefix rdfs: <http://www.w3.org/2000/01/rdf-schema#> .
@prefix xsd:  <http://www.w3.org/2001/XMLSchema#> .
@prefix cogo: <https://cogo.xyz/ns/v1#> .
@prefix cobs: <https://cogo.xyz/instances/codegraph/> .
@prefix ctype: <https://cogo.xyz/instances/semantic-type/> .

`

  rdf += serializeNode(block)
  return rdf
}

function serializeNode(node: unknown): string {
  const b = asRecord(node)
  if (!b) return ""

  const id = stringValue(b.id)
  if (!id) return ""

  const subject = `cobs:ReferoAsset_${sanitizeLocalName(id)}`
  const predicates: string[] = [`cogo:name "${escapeLiteral(id)}"`]

  const styling = asRecord(asRecord(b.options)?.styling)
  if (styling) {
    for (const [key, predicate] of STYLE_PREDICATES) {
      const raw = styling[key]
      if (raw === undefined || raw === null || raw === "") continue
      predicates.push(`${predicate} "${escapeLiteral(valueToLiteral(raw))}"`)
    }
  }

  const children = Array.isArray(b.children) ? b.children : []
  const childSubjects = children
    .map((child) => {
      const childId = stringValue(asRecord(child)?.id)
      return childId ? `cobs:ReferoAsset_${sanitizeLocalName(childId)}` : null
    })
    .filter((child): child is string => Boolean(child))

  if (childSubjects.length > 0) {
    predicates.push(`cogo:hasChild ${childSubjects.join(", ")}`)
  }

  let ttl = `${subject} a ${formatType(b.type)}`
  if (predicates.length === 0) {
    ttl += " .\n\n"
  } else {
    ttl += " ;\n"
    ttl += predicates
      .map((predicate, index) => {
        const suffix = index === predicates.length - 1 ? " ." : " ;"
        return `    ${predicate}${suffix}`
      })
      .join("\n")
    ttl += "\n\n"
  }

  for (const child of children) {
    ttl += serializeNode(child)
  }
  return ttl
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : ""
}

function valueToLiteral(value: unknown): string {
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function formatType(value: unknown): string {
  const raw = stringValue(value)
  if (!raw) return "ctype:container"
  if (/^<[^<>]+>$/.test(raw)) return raw
  if (/^[A-Za-z][A-Za-z0-9+.-]*:[^\s<>]+$/.test(raw)) return raw
  return `ctype:${sanitizeTypeLocalName(raw)}`
}

function sanitizeLocalName(value: string): string {
  const sanitized = value
    .trim()
    .replace(/[:/.-]/g, "_")
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_]/g, "_")
    .replace(/^_+|_+$/g, "")
  if (!sanitized) return "unnamed"
  return /^[A-Za-z_]/.test(sanitized) ? sanitized : `n_${sanitized}`
}

function sanitizeTypeLocalName(value: string): string {
  const sanitized = value
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^A-Za-z0-9_-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
  if (!sanitized) return "container"
  return /^[A-Za-z_]/.test(sanitized) ? sanitized : `type-${sanitized}`
}

function escapeLiteral(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
}
